import React, { useCallback, useMemo, useState } from 'react';
import styled from '@2pulse/responsive';
import { Switch as RNSwitch, Pressable, ActivityIndicator, TextInput } from 'react-native';
import { normalize } from '@app/helpers';
import { 
  IProduct, 
  IPack, 
  ICatalogSnapshotToShop,
  IProductVariation,
  convertToEuros,
  convertToCents
} from '@app/common';
import { theme } from '@app/theme';
import { catalogSnapshotToShopApi } from '@app/store';

interface CatalogShopItemProps {
  item: IProduct | IPack;
  type: 'product' | 'pack';
  catalogSnapshotToShop: ICatalogSnapshotToShop | undefined;
  getLatestCatalogSnapshotToShop: () => ICatalogSnapshotToShop | undefined;
  updateCatalogSnapshotToShopRef: (newData: ICatalogSnapshotToShop['data']) => void;
  onOpenAdvanced: () => void;
}

export const CatalogShopItem = ({
  item,
  type,
  catalogSnapshotToShop,
  getLatestCatalogSnapshotToShop,
  updateCatalogSnapshotToShopRef,
  onOpenAdvanced
}: CatalogShopItemProps) => {
  const [updateCatalogSnapshotToShop, { isLoading }] = catalogSnapshotToShopApi.useUpdateCatalogSnapshotToShopMutation();

  // Get override for this item
  const override = useMemo(() => {
    if (!catalogSnapshotToShop?.data) return null;
    
    if (type === 'product') {
      return catalogSnapshotToShop.data.productsOverrides?.find(
        (o: any) => o.productId === item._id
      );
    } else {
      return catalogSnapshotToShop.data.packsOverrides?.find(
        (o: any) => o.packId === item._id
      );
    }
  }, [catalogSnapshotToShop, item._id, type]);

  // Check if disabled
  const isDisabled = override?.disabled === true;
  const isActive = !isDisabled;

  // Get variations info for products
  const variationsInfo = useMemo(() => {
    if (type !== 'product') return null;
    
    const product = item as IProduct;
    const variations = product.productVariations || [];
    
    return variations.map((pv: IProductVariation) => {
      const variationOverride = override?.productVariations?.find(
        (v: any) => v.productVariationId === pv._id
      );
      const shopPrice = variationOverride?.price;

      return {
        id: pv._id,
        name: pv.variation?.name || 'Standard',
        catalogPrice: pv.price,
        shopPrice: shopPrice !== null && shopPrice !== undefined ? shopPrice : null,
        hasShopPrice: shopPrice !== null && shopPrice !== undefined && shopPrice !== pv.price,
        isDisabled: variationOverride?.disabled === true
      };
    });
  }, [item, type, override]);

  // Get pack price info
  const packPriceInfo = useMemo(() => {
    if (type !== 'pack') return null;
    
    const pack = item as IPack;
    const shopPrice = override?.price;
    
    return {
      catalogPrice: pack.price,
      shopPrice: shopPrice !== null && shopPrice !== undefined ? shopPrice : null,
      hasShopPrice: shopPrice !== null && shopPrice !== undefined && shopPrice !== pack.price
    };
  }, [item, type, override]);

  // Helper: read latest data from ref, apply mutation, update ref synchronously, then dispatch
  const dispatchOverrideUpdate = useCallback(async (
    buildNewData: (latest: ICatalogSnapshotToShop) => ICatalogSnapshotToShop['data']
  ) => {
    const latest = getLatestCatalogSnapshotToShop();
    if (!latest) return;

    const newData = buildNewData(latest);
    // Update ref synchronously so the next mutation reads the correct state
    updateCatalogSnapshotToShopRef(newData);

    await updateCatalogSnapshotToShop({ _id: latest._id, data: newData });
  }, [getLatestCatalogSnapshotToShop, updateCatalogSnapshotToShopRef, updateCatalogSnapshotToShop]);

  // Handle toggle
  const handleToggle = useCallback(async (newValue: boolean) => {
    const newDisabled = !newValue;

    await dispatchOverrideUpdate((latest) => {
      if (type === 'product') {
        const existingOverrides = latest.data?.productsOverrides || [];
        const existingOverride = existingOverrides.find((o: any) => o.productId === item._id);

        const product = item as IProduct;
        const variations = product.productVariations || [];
        const existingVariations = existingOverride?.productVariations || [];

        const updatedVariations = variations.map((pv: IProductVariation) => {
          const existingVar = existingVariations.find((v: any) => v.productVariationId === pv._id);
          return {
            ...existingVar,
            productVariationId: pv._id,
            disabled: newDisabled ? true : null,
          };
        });

        const updatedOverrides = existingOverrides.filter((o: any) => o.productId !== item._id);
        updatedOverrides.push({
          ...existingOverride,
          productId: item._id,
          disabled: newDisabled,
          productVariations: updatedVariations
        });

        return { ...latest.data, productsOverrides: updatedOverrides };
      } else {
        const existingOverrides = latest.data?.packsOverrides || [];
        const existingOverride = existingOverrides.find((o: any) => o.packId === item._id);

        const updatedOverrides = existingOverrides.filter((o: any) => o.packId !== item._id);
        updatedOverrides.push({
          ...existingOverride,
          packId: item._id,
          disabled: newDisabled
        });

        return { ...latest.data, packsOverrides: updatedOverrides };
      }
    });
  }, [dispatchOverrideUpdate, item, type]);

  // Handle pack price update
  const handlePackPriceUpdate = useCallback(async (newPrice: number | null) => {
    await dispatchOverrideUpdate((latest) => {
      const existingOverrides = latest.data?.packsOverrides || [];
      const existingOverride = existingOverrides.find((o: any) => o.packId === item._id);

      const updatedOverrides = existingOverrides.filter((o: any) => o.packId !== item._id);
      updatedOverrides.push({
        ...existingOverride,
        packId: item._id,
        disabled: existingOverride?.disabled ?? null,
        price: newPrice
      });

      return { ...latest.data, packsOverrides: updatedOverrides };
    });
  }, [dispatchOverrideUpdate, item._id]);

  // Handle variation price update
  const handleVariationPriceUpdate = useCallback(async (variationId: string, newPrice: number | null) => {
    await dispatchOverrideUpdate((latest) => {
      const existingOverrides = latest.data?.productsOverrides || [];
      const existingOverride = existingOverrides.find((o: any) => o.productId === item._id);

      const existingVariations = existingOverride?.productVariations || [];
      const updatedVariations = existingVariations.filter((v: any) => v.productVariationId !== variationId);

      const existingVariation = existingVariations.find((v: any) => v.productVariationId === variationId);
      updatedVariations.push({
        ...existingVariation,
        productVariationId: variationId,
        price: newPrice
      });

      const updatedOverrides = existingOverrides.filter((o: any) => o.productId !== item._id);
      updatedOverrides.push({
        ...existingOverride,
        productId: item._id,
        disabled: existingOverride?.disabled ?? null,
        productVariations: updatedVariations
      });

      return { ...latest.data, productsOverrides: updatedOverrides };
    });
  }, [dispatchOverrideUpdate, item._id]);

  // Handle variation toggle
  const handleVariationToggle = useCallback(async (variationId: string, newValue: boolean) => {
    const newDisabled = !newValue;

    await dispatchOverrideUpdate((latest) => {
      const existingOverrides = latest.data?.productsOverrides || [];
      const existingOverride = existingOverrides.find((o: any) => o.productId === item._id);

      const existingVariations = existingOverride?.productVariations || [];
      const updatedVariations = existingVariations.filter((v: any) => v.productVariationId !== variationId);

      const existingVariation = existingVariations.find((v: any) => v.productVariationId === variationId);
      updatedVariations.push({
        ...existingVariation,
        productVariationId: variationId,
        disabled: newDisabled || null,
      });

      const updatedOverrides = existingOverrides.filter((o: any) => o.productId !== item._id);
      updatedOverrides.push({
        ...existingOverride,
        productId: item._id,
        disabled: existingOverride?.disabled ?? null,
        productVariations: updatedVariations
      });

      return { ...latest.data, productsOverrides: updatedOverrides };
    });
  }, [dispatchOverrideUpdate, item._id]);

  const category = item.tags?.[0]?.name || '-';
  const hasMultipleVariations = variationsInfo && variationsInfo.length > 1;

  return (
    <ItemWrapper disabled={isDisabled}>
      {/* Main row */}
      <ItemContainer disabled={isDisabled}>
        <SwitchContainer>
          {isLoading ? (
            <LoadingIndicator>
              <ActivityIndicator size="small" color={theme.colors.celadonblue} />
            </LoadingIndicator>
          ) : (
            <Pressable 
              onPress={(e) => {
                e.stopPropagation();
                handleToggle(!isActive);
              }}
              disabled={!catalogSnapshotToShop}
            >
              <RNSwitch
                value={isActive}
                onValueChange={handleToggle}
                disabled={!catalogSnapshotToShop}
                thumbColor={isActive ? theme.colors.celadonblue : theme.colors.satanGray}
                trackColor={{
                  false: 'rgba(255,255,255,0.1)',
                  true: 'rgba(0, 190, 230, 0.3)'
                }}
                pointerEvents="none"
              />
            </Pressable>
          )}
        </SwitchContainer>

        <NameContainer>
          <ItemName disabled={isDisabled}>{item.name}</ItemName>
          {isDisabled && (
            <DisabledBadge>
              <DisabledBadgeText>Désactivé</DisabledBadgeText>
            </DisabledBadge>
          )}
        </NameContainer>

        <CategoryContainer>
          <CategoryText disabled={isDisabled}>{category}</CategoryText>
        </CategoryContainer>

        <PriceEditContainer>
          {/* Pack price - editable inline */}
          {type === 'pack' && packPriceInfo && (
            <InlinePriceEditor
              catalogPrice={packPriceInfo.catalogPrice}
              shopPrice={packPriceInfo.shopPrice}
              onSave={handlePackPriceUpdate}
              disabled={isDisabled || !catalogSnapshotToShop}
            />
          )}
          
          {/* Product with single variation - editable inline */}
          {type === 'product' && variationsInfo && variationsInfo.length === 1 && (
            <InlinePriceEditor
              catalogPrice={variationsInfo[0].catalogPrice}
              shopPrice={variationsInfo[0].shopPrice}
              onSave={(price) => handleVariationPriceUpdate(variationsInfo[0].id, price)}
              disabled={isDisabled || !catalogSnapshotToShop}
            />
          )}
          
          {/* Product with multiple variations - show count */}
          {hasMultipleVariations && (
            <VariationsCount disabled={isDisabled}>
              {variationsInfo.length} déclinaisons
            </VariationsCount>
          )}
        </PriceEditContainer>

        <AdvancedButton onPress={onOpenAdvanced} disabled={isDisabled}>
          <AdvancedButtonText disabled={isDisabled}>⚙</AdvancedButtonText>
        </AdvancedButton>
      </ItemContainer>

      {/* Variations rows (expanded) with inline editing */}
      {hasMultipleVariations && (
        <VariationsContainer>
          {variationsInfo.map((variation: any) => {
            const variationDisabled = isDisabled || variation.isDisabled;
            return (
              <VariationRow key={variation.id} disabled={variationDisabled}>
                <VariationSwitchContainer>
                  <Pressable
                    onPress={(e) => {
                      e.stopPropagation();
                      handleVariationToggle(variation.id, variation.isDisabled);
                    }}
                    disabled={isDisabled || !catalogSnapshotToShop}
                  >
                    <RNSwitch
                      value={!variationDisabled}
                      onValueChange={(val) => handleVariationToggle(variation.id, val)}
                      disabled={isDisabled || !catalogSnapshotToShop}
                      thumbColor={!variationDisabled ? theme.colors.celadonblue : theme.colors.satanGray}
                      trackColor={{
                        false: 'rgba(255,255,255,0.1)',
                        true: 'rgba(0, 190, 230, 0.3)'
                      }}
                      pointerEvents="none"
                      style={{ transform: [{ scale: 0.8 }] }}
                    />
                  </Pressable>
                </VariationSwitchContainer>
                <VariationName disabled={variationDisabled}>
                  ↳ {variation.name}
                </VariationName>
                {variation.isDisabled && !isDisabled && (
                  <DisabledBadge>
                    <DisabledBadgeText>Désactivé</DisabledBadgeText>
                  </DisabledBadge>
                )}
                <VariationPriceContainer>
                  <InlinePriceEditor
                    catalogPrice={variation.catalogPrice}
                    shopPrice={variation.shopPrice}
                    onSave={(price) => handleVariationPriceUpdate(variation.id, price)}
                    disabled={variationDisabled || !catalogSnapshotToShop}
                    compact
                  />
                </VariationPriceContainer>
              </VariationRow>
            );
          })}
        </VariationsContainer>
      )}
    </ItemWrapper>
  );
};

// Inline price editor component
interface InlinePriceEditorProps {
  catalogPrice: number | null | undefined;
  shopPrice: number | null | undefined;
  onSave: (price: number | null) => void;
  disabled?: boolean;
  compact?: boolean;
}

const InlinePriceEditor = ({ catalogPrice, shopPrice, onSave, disabled, compact }: InlinePriceEditorProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [inputValue, setInputValue] = useState('');

  const catalogPriceEuros = catalogPrice ? convertToEuros(catalogPrice) : 0;
  const shopPriceEuros = shopPrice !== null && shopPrice !== undefined ? convertToEuros(shopPrice) : null;
  const hasShopPrice = shopPriceEuros !== null && shopPriceEuros !== catalogPriceEuros;

  const handleStartEdit = () => {
    if (disabled) return;
    const currentPrice = shopPriceEuros ?? catalogPriceEuros;
    setInputValue(currentPrice.toFixed(2));
    setIsEditing(true);
  };

  const handleSave = () => {
    const newPriceEuros = parseFloat(inputValue);
    if (!isNaN(newPriceEuros)) {
      const newPriceCents = convertToCents(newPriceEuros);
      // If same as catalog, reset to null
      if (newPriceCents === catalogPrice) {
        onSave(null);
      } else {
        onSave(newPriceCents);
      }
    }
    setIsEditing(false);
  };

  const handleReset = () => {
    onSave(null);
    setIsEditing(false);
  };

  if (catalogPrice === null || catalogPrice === undefined) {
    return <PriceText disabled={!!disabled}>-</PriceText>;
  }

  if (isEditing) {
    return (
      <EditContainer compact={compact}>
        <PriceInputContainer>
          <PriceInput
            value={inputValue}
            onChangeText={setInputValue}
            keyboardType="decimal-pad"
            autoFocus
            selectTextOnFocus
            onBlur={handleSave}
            onSubmitEditing={handleSave}
          />
          <EuroSymbol>€</EuroSymbol>
        </PriceInputContainer>
        {hasShopPrice && (
          <ResetButton onPress={handleReset}>
            <ResetButtonText>×</ResetButtonText>
          </ResetButton>
        )}
      </EditContainer>
    );
  }

  return (
    <PriceDisplayContainer onPress={handleStartEdit} disabled={disabled}>
      <CatalogPriceText strikethrough={hasShopPrice} disabled={!!disabled}>
        {catalogPriceEuros.toFixed(2)} €
      </CatalogPriceText>
      {hasShopPrice && (
        <>
          <PriceArrow disabled={!!disabled}>→</PriceArrow>
          <ShopPriceBadge>
            <ShopPriceText>{shopPriceEuros?.toFixed(2)} €</ShopPriceText>
          </ShopPriceBadge>
        </>
      )}
      {!hasShopPrice && !disabled && (
        <EditIcon>✎</EditIcon>
      )}
    </PriceDisplayContainer>
  );
};

// Styled components
const ItemWrapper = styled.View<{ disabled: boolean }>(({ props }) => ({
  marginBottom: normalize(8),
  opacity: props.disabled ? 0.6 : 1,
}));

const ItemContainer = styled.View<{ disabled: boolean }>(({ theme, props }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  height: normalize(56),
  paddingHorizontal: normalize(16),
  backgroundColor: props.disabled ? 'rgba(255,255,255,0.02)' : 'rgba(255,255,255,0.05)',
  borderRadius: normalize(8),
}));

const SwitchContainer = styled.View({
  width: normalize(60),
  alignItems: 'flex-start',
});

const LoadingIndicator = styled.View({
  width: normalize(51),
  height: normalize(31),
  justifyContent: 'center',
  alignItems: 'center',
});

const NameContainer = styled.View({
  flex: 1,
  flexDirection: 'row',
  alignItems: 'center',
  gap: normalize(8),
});

const ItemName = styled.Text<{ disabled: boolean }>(({ theme, props }) => ({
  fontFamily: 'Work Sans',
  fontSize: normalize(15),
  fontWeight: '500',
  color: theme.colors.babypowder,
  textDecorationLine: props.disabled ? 'line-through' : 'none',
}));

const DisabledBadge = styled.View(({ theme }) => ({
  backgroundColor: theme.colors.crimson,
  borderRadius: normalize(4),
  paddingHorizontal: normalize(6),
  paddingVertical: normalize(2),
}));

const DisabledBadgeText = styled.Text({
  fontFamily: 'Work Sans',
  fontSize: normalize(10),
  fontWeight: '600',
  color: '#FFF',
});

const CategoryContainer = styled.View({
  width: normalize(140),
});

const CategoryText = styled.Text<{ disabled: boolean }>(({ theme, props }) => ({
  fontFamily: 'Work Sans',
  fontSize: normalize(13),
  color: props.disabled ? 'rgba(255,255,255,0.4)' : theme.colors.babypowder,
  opacity: 0.7,
}));

const PriceEditContainer = styled.View({
  width: normalize(200),
  alignItems: 'flex-end',
});

const AdvancedButton = styled.TouchableOpacity<{ disabled?: boolean }>(({ props }) => ({
  width: normalize(36),
  height: normalize(36),
  borderRadius: normalize(8),
  backgroundColor: props.disabled ? 'rgba(255,255,255,0.02)' : 'rgba(255,255,255,0.08)',
  alignItems: 'center',
  justifyContent: 'center',
  marginLeft: normalize(8),
}));

const AdvancedButtonText = styled.Text<{ disabled?: boolean }>(({ theme, props }) => ({
  fontSize: normalize(16),
  color: props.disabled ? 'rgba(255,255,255,0.3)' : theme.colors.babypowder,
}));

const PriceText = styled.Text<{ disabled: boolean }>(({ theme, props }) => ({
  fontFamily: 'Work Sans',
  fontSize: normalize(14),
  color: props.disabled ? 'rgba(255,255,255,0.4)' : theme.colors.babypowder,
}));

const VariationsCount = styled.Text<{ disabled: boolean }>(({ theme, props }) => ({
  fontFamily: 'Work Sans',
  fontSize: normalize(12),
  color: props.disabled ? 'rgba(255,255,255,0.4)' : theme.colors.celadonblue,
  fontWeight: '500',
}));

// Variations expanded
const VariationsContainer = styled.View({
  marginTop: normalize(4),
  paddingLeft: normalize(76),
  gap: normalize(2),
});

const VariationRow = styled.View<{ disabled?: boolean }>(({ theme, props }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  height: normalize(44),
  paddingHorizontal: normalize(16),
  backgroundColor: 'rgba(255,255,255,0.02)',
  borderRadius: normalize(6),
  opacity: props?.disabled ? 0.6 : 1,
}));

const VariationSwitchContainer = styled.View({
  width: normalize(48),
  alignItems: 'flex-start',
});

const VariationName = styled.Text<{ disabled: boolean }>(({ theme, props }) => ({
  fontFamily: 'Work Sans',
  fontSize: normalize(13),
  color: props.disabled ? 'rgba(255,255,255,0.4)' : theme.colors.babypowder,
  flex: 1,
  opacity: 0.8,
}));

const VariationPriceContainer = styled.View({
  width: normalize(220),
  alignItems: 'flex-end',
});

// Inline editor styles
const EditContainer = styled.View<{ compact?: boolean }>(({ props }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: normalize(8),
}));

const PriceInputContainer = styled.View({
  flexDirection: 'row',
  alignItems: 'center',
  backgroundColor: 'rgba(255,255,255,0.15)',
  borderRadius: normalize(6),
  paddingHorizontal: normalize(10),
  height: normalize(36),
  borderWidth: 2,
  borderColor: theme.colors.celadonblue,
});

const PriceInput = styled.TextInput({
  fontFamily: 'Work Sans',
  fontSize: normalize(14),
  fontWeight: '600',
  color: theme.colors.babypowder,
  width: normalize(60),
  textAlign: 'right',
  padding: 0,
});

const EuroSymbol = styled.Text({
  fontFamily: 'Work Sans',
  fontSize: normalize(14),
  color: theme.colors.babypowder,
  marginLeft: normalize(4),
});

const ResetButton = styled.TouchableOpacity({
  width: normalize(28),
  height: normalize(28),
  borderRadius: normalize(14),
  backgroundColor: 'rgba(255,100,100,0.3)',
  alignItems: 'center',
  justifyContent: 'center',
});

const ResetButtonText = styled.Text({
  fontFamily: 'Work Sans',
  fontSize: normalize(18),
  color: '#FF6B6B',
  fontWeight: '600',
});

const PriceDisplayContainer = styled.TouchableOpacity<{ disabled?: boolean }>(({ props }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: normalize(8),
  paddingVertical: normalize(6),
  paddingHorizontal: normalize(10),
  borderRadius: normalize(6),
  backgroundColor: props.disabled ? 'transparent' : 'rgba(255,255,255,0.03)',
}));

const CatalogPriceText = styled.Text<{ strikethrough?: boolean; disabled: boolean }>(({ theme, props }) => ({
  fontFamily: 'Work Sans',
  fontSize: normalize(14),
  color: props.strikethrough 
    ? (props.disabled ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.5)')
    : (props.disabled ? 'rgba(255,255,255,0.4)' : theme.colors.babypowder),
  textDecorationLine: props.strikethrough ? 'line-through' : 'none',
}));

const PriceArrow = styled.Text<{ disabled: boolean }>(({ theme, props }) => ({
  fontFamily: 'Work Sans',
  fontSize: normalize(14),
  color: props.disabled ? 'rgba(255,255,255,0.3)' : theme.colors.gold,
  fontWeight: '600',
}));

const ShopPriceBadge = styled.View(({ theme }) => ({
  backgroundColor: 'rgba(255,215,0,0.2)',
  borderRadius: normalize(6),
  paddingHorizontal: normalize(10),
  paddingVertical: normalize(4),
  borderWidth: 1,
  borderColor: theme.colors.gold,
}));

const ShopPriceText = styled.Text(({ theme }) => ({
  fontFamily: 'Work Sans',
  fontSize: normalize(14),
  fontWeight: '700',
  color: theme.colors.gold,
}));

const EditIcon = styled.Text(({ theme }) => ({
  fontFamily: 'Work Sans',
  fontSize: normalize(12),
  color: 'rgba(255,255,255,0.3)',
  marginLeft: normalize(4),
}));
